import React, { useState, useEffect, useRef } from 'react';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, Copy, Check, UserPlus, Settings, Monitor, MessageSquare, Hand, MoreVertical, Link2, ShieldX, ArrowRight, ChevronRight, Clock, FileText, Search, Trash2, Play } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

// Allowed roles for conference access
const ALLOWED_ROLES = ['executive', 'superadmin'];

const ConferencePage = ({ getAuthHeaders, currentUser, roomId: propRoomId }) => {
  // Check if user has permission to access conferences
  const hasAccess = currentUser && ALLOWED_ROLES.includes(currentUser.role);
  const [roomId, setRoomId] = useState(propRoomId || null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [isGuest, setIsGuest] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomType, setNewRoomType] = useState('video');
  const [isCustomerCall, setIsCustomerCall] = useState(false);
  const [activeConferences, setActiveConferences] = useState([]);
  
  // New features state
  const [meetingTimer, setMeetingTimer] = useState(0);
  const [handRaised, setHandRaised] = useState(false);
  const [raisedHands, setRaisedHands] = useState([]);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: '', description: '', scheduled_start: '', participants: [], call_type: 'video' });
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [showRecordingsPanel, setShowRecordingsPanel] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [recordingsQuery, setRecordingsQuery] = useState('');
  const [recordingsSearch, setRecordingsSearch] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState(null);
  // Live reactions overlay
  const [reactions, setReactions] = useState([]); // [{id, emoji, from, x}]
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const speechRecogRef = useRef(null);
  const liveTranscriptRef = useRef('');
  const recordingStartRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const meetingTimerRef = useRef(null);

  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const wsRef = useRef(null);

  // Get room ID from URL if not provided as prop
  useEffect(() => {
    if (!propRoomId) {
      const path = window.location.pathname;
      const match = path.match(/\/conference\/([^/]+)/);
      if (match) {
        setRoomId(match[1]);
      }
    }
  }, [propRoomId]);

  // Fetch active conferences on mount
  useEffect(() => {
    if (currentUser) {
      fetchActiveConferences();
      fetchUpcomingMeetings();
    }
  }, [currentUser]);

  // Meeting timer
  useEffect(() => {
    if (isJoined) {
      meetingTimerRef.current = setInterval(() => setMeetingTimer(t => t + 1), 1000);
    }
    return () => { if (meetingTimerRef.current) clearInterval(meetingTimerRef.current); };
  }, [isJoined]);

  const formatTimer = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };

  const toggleHandRaise = () => {
    setHandRaised(!handRaised);
    if (!handRaised) {
      setRaisedHands(prev => [...prev, currentUser?.display_name || currentUser?.username]);
      toast.success('Hand raised');
    } else {
      setRaisedHands(prev => prev.filter(n => n !== (currentUser?.display_name || currentUser?.username)));
    }
  };

  // ─── Recording (client-side MediaRecorder + Web Speech transcript) ───
  const startRecording = async () => {
    try {
      // Reuse the existing local mic stream if available; else request one
      let stream = localStreamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const mr = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      liveTranscriptRef.current = '';
      recordingStartRef.current = new Date().toISOString();
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.start(1000);
      mediaRecorderRef.current = mr;

      // Live transcription via Web Speech (browser-native, no external key)
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = false;
        rec.lang = 'en-GB';
        rec.onresult = (evt) => {
          for (let i = evt.resultIndex; i < evt.results.length; i++) {
            if (evt.results[i].isFinal) liveTranscriptRef.current += evt.results[i][0].transcript + ' ';
          }
        };
        rec.onerror = () => {};
        try { rec.start(); } catch {}
        speechRecogRef.current = rec;
      }
      setIsRecording(true);
      setRecordingTimer(0);
      recordingTimerRef.current = setInterval(() => setRecordingTimer(t => t + 1), 1000);
      toast.success('Recording started');
    } catch (e) {
      toast.error('Could not start recording — check mic permission');
    }
  };

  const stopRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (speechRecogRef.current) {
        try { speechRecogRef.current.stop(); } catch {}
      }
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
      const duration = recordingTimer;
      const transcript = (liveTranscriptRef.current || '').trim() || '(No speech captured — recording saved without transcript)';
      const headers = getAuthHeaders();
      if (headers && roomId) {
        await axios.post(`${BACKEND_URL}/api/calling/conference/${roomId}/recording`, {
          room_id: roomId,
          room_name: roomInfo?.name || '',
          transcript,
          duration_seconds: duration,
          participants,
          started_at: recordingStartRef.current,
          call_type: roomInfo?.call_type || 'video',
        }, { headers });
      }
      setIsRecording(false);
      setRecordingTimer(0);
      toast.success('Recording saved with transcript');
      fetchRecordings();
    } catch (e) {
      setIsRecording(false);
      toast.error('Failed to save recording');
    }
  };

  const toggleRecording = () => { isRecording ? stopRecording() : startRecording(); };

  // ═══ Live Emoji Reactions ═══
  const spawnReaction = (emoji, from) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const x = 15 + Math.random() * 70; // horizontal % across the video area
    setReactions(prev => [...prev, { id, emoji, from, x }]);
    setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 3200);
  };
  const sendReaction = (emoji) => {
    spawnReaction(emoji, currentUser?.display_name || currentUser?.username || 'You');
    setShowReactionPicker(false);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'conference-reaction',
          room_id: roomId,
          emoji,
          username: currentUser?.username || 'guest',
          display_name: currentUser?.display_name || currentUser?.username || 'Guest',
        }));
      }
    } catch {}
  };

  const fetchRecordings = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers || !roomId) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/conference/${roomId}/recordings`, { headers });
      setRecordings(res.data?.recordings || []);
    } catch {}
  };

  const searchRecordings = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const q = (recordingsQuery || '').trim();
      const res = await axios.get(`${BACKEND_URL}/api/calling/conference-recordings/search`, { params: { q }, headers });
      setRecordingsSearch(res.data?.recordings || []);
    } catch {}
  };

  const deleteRecording = async (recId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/calling/conference/recording/${recId}`, { headers });
      setRecordings(prev => prev.filter(r => r.id !== recId));
      setRecordingsSearch(prev => prev.filter(r => r.id !== recId));
      toast.success('Recording deleted');
    } catch { toast.error('Delete failed'); }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !roomId) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/conference/chat`, { room_id: roomId, message: chatInput }, { headers });
      setChatMessages(prev => [...prev, {
        sender_name: currentUser?.display_name || currentUser?.username,
        message: chatInput,
        timestamp: new Date().toISOString()
      }]);
      setChatInput('');
    } catch {}
  };

  const saveMeetingNotes = async () => {
    if (!roomId) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/conference/notes`, { room_id: roomId, notes: meetingNotes }, { headers });
      toast.success('Notes saved');
    } catch {}
  };

  const fetchMeetingChat = async () => {
    if (!roomId) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/conference/chat/${roomId}`, { headers });
      setChatMessages(res.data || []);
    } catch {}
  };

  const fetchMeetingNotes = async () => {
    if (!roomId) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/conference/notes/${roomId}`, { headers });
      setMeetingNotes(res.data?.notes || '');
    } catch {}
  };

  const fetchUpcomingMeetings = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/meetings/upcoming`, { headers });
      setUpcomingMeetings(res.data || []);
    } catch {}
  };

  const handleScheduleMeeting = async () => {
    if (!newMeeting.title || !newMeeting.scheduled_start) { toast.error('Title and time required'); return; }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/meetings/schedule`, newMeeting, { headers });
      setShowScheduleModal(false);
      setNewMeeting({ title: '', description: '', scheduled_start: '', participants: [], call_type: 'video' });
      fetchUpcomingMeetings();
      toast.success('Meeting scheduled');
    } catch { toast.error('Failed to schedule'); }
  };

  // Fetch room info when roomId changes
  useEffect(() => {
    if (roomId) {
      fetchRoomInfo();
    }
  }, [roomId]);

  const fetchActiveConferences = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/calling/conferences/active`, { headers });
      setActiveConferences(response.data);
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchRoomInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/calling/conference/${roomId}`);
      setRoomInfo(response.data);
    } catch (error) {
      toast.error('Conference room not found');
    }
  };

  const createConference = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.post(`${BACKEND_URL}/api/calling/conference/create`, {
        name: newRoomName || `${currentUser?.display_name || currentUser?.username}'s Meeting`,
        call_type: newRoomType,
        max_participants: 10,
        is_customer_call: isCustomerCall
      }, { headers });

      setRoomId(response.data.room_id);
      setRoomInfo(response.data.data);
      setShowCreateModal(false);
      setNewRoomName('');
      toast.success('Conference created!');
      
      // Auto-join after creating
      setTimeout(() => joinConference(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create conference');
    }
  };

  const joinConference = async () => {
    if (!roomId) return;

    try {
      let response;
      
      if (currentUser) {
        const headers = getAuthHeaders();
        response = await axios.post(`${BACKEND_URL}/api/calling/conference/${roomId}/join`, {}, { headers });
      } else if (guestName) {
        response = await axios.post(`${BACKEND_URL}/api/calling/conference/${roomId}/guest-join`, null, {
          params: { display_name: guestName }
        });
        setIsGuest(true);
      } else {
        toast.error('Please enter your name to join');
        return;
      }

      setIsJoined(true);
      setParticipants(response.data.participants || []);
      
      // Start local media
      await startLocalMedia();
      
      // Connect to WebSocket for signaling
      connectWebSocket();
      
      toast.success('Joined conference!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join conference');
    }
  };

  const leaveConference = async () => {
    try {
      if (currentUser && !isGuest) {
        const headers = getAuthHeaders();
        await axios.post(`${BACKEND_URL}/api/calling/conference/${roomId}/leave`, {}, { headers });
      }
    } catch (error) {
      console.error('Error leaving:', error);
    }

    cleanup();
    setIsJoined(false);
    setRoomId(null);
    setRoomInfo(null);
    toast.info('Left conference');
  };

  const endConference = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/calling/conference/${roomId}/end`, {}, { headers });
      cleanup();
      setIsJoined(false);
      setRoomId(null);
      setRoomInfo(null);
      toast.success('Conference ended');
    } catch (error) {
      toast.error('Failed to end conference');
    }
  };

  const startLocalMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: roomInfo?.call_type === 'video'
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing media:', error);
      toast.error('Could not access camera/microphone');
    }
  };

  const connectWebSocket = () => {
    if (!currentUser) return;
    
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/calling/ws/${currentUser.username}`);
    
    ws.onopen = () => {
      console.log('Conference WebSocket connected');
    };

    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'participant_joined':
        case 'guest_joined':
          handleParticipantJoined(data);
          break;
        case 'participant_left':
          handleParticipantLeft(data);
          break;
        case 'conference_ended':
          handleConferenceEnded();
          break;
        case 'conference-offer':
          await handleConferenceOffer(data);
          break;
        case 'conference-answer':
          await handleConferenceAnswer(data);
          break;
        case 'conference-ice':
          await handleConferenceIce(data);
          break;
        case 'conference-reaction':
          spawnReaction(data.emoji, data.display_name || data.username || 'Someone');
          break;
        default:
          break;
      }
    };

    wsRef.current = ws;
  };

  const handleParticipantJoined = async (data) => {
    const newParticipant = data.username || data.guest_id;
    setParticipants(prev => [...prev.filter(p => p !== newParticipant), newParticipant]);
    toast.info(`${data.display_name || newParticipant} joined`);
    
    // Create peer connection for new participant
    await createPeerConnection(newParticipant, true);
  };

  const handleParticipantLeft = (data) => {
    setParticipants(prev => prev.filter(p => p !== data.username));
    toast.info(`${data.username} left`);
    
    // Clean up peer connection
    if (peerConnectionsRef.current[data.username]) {
      peerConnectionsRef.current[data.username].close();
      delete peerConnectionsRef.current[data.username];
    }
  };

  const handleConferenceEnded = () => {
    cleanup();
    setIsJoined(false);
    toast.info('Conference has ended');
  };

  const createPeerConnection = async (participantId, isInitiator) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionsRef.current[participantId] = pc;

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle remote tracks
    pc.ontrack = (event) => {
      const remoteVideo = document.getElementById(`video-${participantId}`);
      if (remoteVideo) {
        remoteVideo.srcObject = event.streams[0];
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        wsRef.current?.send(JSON.stringify({
          type: 'conference-ice',
          room_id: roomId,
          target: participantId,
          candidate: event.candidate
        }));
      }
    };

    // If initiator, create offer
    if (isInitiator) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({
        type: 'conference-offer',
        room_id: roomId,
        sdp: offer
      }));
    }
  };

  const handleConferenceOffer = async (data) => {
    if (!peerConnectionsRef.current[data.from]) {
      await createPeerConnection(data.from, false);
    }
    const pc = peerConnectionsRef.current[data.from];
    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    wsRef.current?.send(JSON.stringify({
      type: 'conference-answer',
      room_id: roomId,
      target: data.from,
      sdp: answer
    }));
  };

  const handleConferenceAnswer = async (data) => {
    const pc = peerConnectionsRef.current[data.from];
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
    }
  };

  const handleConferenceIce = async (data) => {
    const pc = peerConnectionsRef.current[data.from];
    if (pc && data.candidate) {
      await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
    }
  };

  const cleanup = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const copyInviteLink = () => {
    const link = `${FRONTEND_URL}/conference/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  // Access denied view for unauthorized users
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full bg-gradient-to-br from-gray-900/90 to-black border-red-500/30">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/10 mx-auto mb-6 flex items-center justify-center">
              <ShieldX className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Access Restricted</h2>
            <p className="text-gray-400 mb-4">
              Conference management is only available to Executives and Super Admins.
            </p>
            <p className="text-gray-500 text-sm">
              Contact your administrator if you need access to this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Landing view - no room selected — JEWEL
  if (!roomId && !isJoined) {
    return (
      <div className="space-y-6 relative">
        {/* Ambient glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute -top-32 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
          <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
        </div>

        {/* Premium Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
          <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }} />
          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="relative w-14 h-14 flex-shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
                <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                  <Video className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#0a0a14] shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Live · Meeting Suite</p>
                <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                  Conference <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Room</span>
                </h1>
                <p className="text-white/50 text-sm mt-1">Start, schedule, or drop into a room in seconds</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-300 text-[11px] tracking-[0.1em] uppercase">{activeConferences.length} live</span>
              </div>
              <Button onClick={() => setShowScheduleModal(true)} size="sm" data-testid="schedule-meeting-btn"
                className="bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/[0.08] hover:text-white h-9 text-xs gap-1.5 rounded-xl transition-all">
                <Clock className="w-3.5 h-3.5" /> Schedule
              </Button>
              <Button onClick={() => setShowCreateModal(true)} size="sm" data-testid="new-conference-btn"
                className="group relative bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white h-9 text-xs gap-1.5 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:-translate-y-0.5 transition-all rounded-xl overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Video className="w-3.5 h-3.5 group-hover:rotate-6 transition-transform" /> New conference
              </Button>
            </div>
          </div>
        </div>

        {/* Active Conferences — Jewel Grid */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md p-6">
          <div className="pointer-events-none absolute -top-24 right-1/3 w-60 h-40 bg-cyan-500/[0.04] rounded-full blur-[80px]" />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/10 border border-cyan-500/25 flex items-center justify-center">
                <Users className="w-4 h-4 text-cyan-300" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Live now</p>
                <h2 className="text-white text-base font-medium tracking-tight">Active Conferences</h2>
              </div>
            </div>
          </div>
          {activeConferences.length === 0 ? (
            <div className="text-center py-14">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.02] border border-white/[0.06] mb-4">
                <Video className="w-7 h-7 text-white/25" strokeWidth={1.5} />
              </div>
              <p className="text-white/50 text-sm tracking-wide">No active conferences right now</p>
              <p className="text-white/30 text-xs mt-1">Start one above to bring the team together</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {activeConferences.map(conf => (
                <button key={conf.room_id} onClick={() => setRoomId(conf.room_id)}
                  className="group text-left p-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] hover:border-cyan-400/40 hover:bg-white/[0.04] transition-all overflow-hidden relative">
                  <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-cyan-500/[0.08] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/25 to-cyan-500/15 border border-emerald-500/25 flex items-center justify-center">
                        {conf.call_type === 'video' ? <Video className="w-4 h-4 text-emerald-300" /> : <Mic className="w-4 h-4 text-emerald-300" />}
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-emerald-300 text-[10px] tracking-[0.1em] uppercase font-medium">Live</span>
                      </span>
                    </div>
                    {conf.is_customer_call && (
                      <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/25 text-[9px] tracking-wide">Customer</Badge>
                    )}
                  </div>
                  <h3 className="text-white font-medium mb-1 truncate">{conf.name}</h3>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/50 flex items-center gap-1"><Users className="w-3 h-3" /> {conf.participants}/{conf.max_participants}</span>
                    <span className="text-cyan-300/80 group-hover:text-cyan-300 inline-flex items-center gap-1 text-[11px] font-medium">Join <ChevronRight className="w-3 h-3" /></span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Join by Room ID — Jewel */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md p-6">
          <div className="pointer-events-none absolute -bottom-16 left-1/3 w-60 h-40 bg-violet-500/[0.06] rounded-full blur-[80px]" />
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-500/10 border border-violet-500/25 flex items-center justify-center">
              <Link2 className="w-4 h-4 text-violet-300" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Invited?</p>
              <h2 className="text-white text-base font-medium tracking-tight">Join by Room ID</h2>
            </div>
          </div>
          <div className="flex gap-3">
            <JewelInput placeholder="Paste room ID or invite link…" value={roomId || ''} onChange={(e) => setRoomId(e.target.value)} icon={Link2} />
            <JewelButton accent="violet" onClick={() => roomId && fetchRoomInfo()} disabled={!roomId} icon={ArrowRight}>Join</JewelButton>
          </div>
        </div>

        {/* Upcoming Meetings — Jewel */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/25 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-300" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">On the calendar</p>
                <h2 className="text-white text-base font-medium tracking-tight">Upcoming Meetings</h2>
              </div>
            </div>
            <JewelButton accent="amber" onClick={() => setShowScheduleModal(true)} icon={UserPlus} data-testid="schedule-meeting-btn-2">Schedule</JewelButton>
          </div>
          {upcomingMeetings.length === 0 ? (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.02] border border-white/[0.06] mb-3">
                <Clock className="w-6 h-6 text-white/25" strokeWidth={1.5} />
              </div>
              <p className="text-white/50 text-sm">No upcoming meetings</p>
              <p className="text-white/30 text-xs mt-1">Schedule one to lock the room in advance</p>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingMeetings.map(m => (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-amber-400/30 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
                      <Video className="w-5 h-5 text-amber-300" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{m.title}</p>
                      <p className="text-white/45 text-[11px]">{new Date(m.scheduled_start).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      {m.description && <p className="text-white/30 text-[10px] mt-0.5 truncate max-w-xs">{m.description}</p>}
                    </div>
                  </div>
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/25 text-[10px] tracking-wide uppercase">{m.call_type}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule + Create Modals rendered from here */}
        {/* Schedule Meeting Modal — Jewel */}
        <JewelModal
          open={showScheduleModal}
          onOpenChange={setShowScheduleModal}
          icon={Clock}
          title="Schedule a Meeting"
          subtitle="Plan a moment your team can prepare for"
          accent="amber"
          size="md"
          testid="schedule-meeting-modal"
          footer={<>
            <JewelButton variant="ghost" onClick={() => setShowScheduleModal(false)}>Cancel</JewelButton>
            <JewelButton accent="amber" onClick={handleScheduleMeeting} data-testid="confirm-schedule-btn">Schedule</JewelButton>
          </>}
        >
          <div className="space-y-4">
            <JewelField label="Meeting title" required>
              <JewelInput value={newMeeting.title} onChange={e => setNewMeeting(p => ({ ...p, title: e.target.value }))}
                placeholder="Q4 kickoff, weekly sync…" data-testid="schedule-title-input" autoFocus />
            </JewelField>
            <JewelField label="Description">
              <JewelTextarea value={newMeeting.description} onChange={e => setNewMeeting(p => ({ ...p, description: e.target.value }))}
                placeholder="Agenda, links, prep notes…" rows={2} />
            </JewelField>
            <div className="grid grid-cols-2 gap-3">
              <JewelField label="Start" required>
                <JewelInput type="datetime-local" value={newMeeting.scheduled_start} onChange={e => setNewMeeting(p => ({ ...p, scheduled_start: e.target.value }))}
                  data-testid="schedule-datetime-input" />
              </JewelField>
              <JewelField label="Call type">
                <JewelSelect value={newMeeting.call_type} onChange={e => setNewMeeting(p => ({ ...p, call_type: e.target.value }))}>
                  <option value="video">Video call</option>
                  <option value="audio">Audio only</option>
                </JewelSelect>
              </JewelField>
            </div>
          </div>
        </JewelModal>

        {/* Create Conference Modal — Jewel */}
        <JewelModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          icon={Video}
          title="New Conference"
          subtitle="Spin up a room in seconds"
          accent="cyan"
          size="md"
          testid="create-conference-modal"
          footer={<>
            <JewelButton variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</JewelButton>
            <JewelButton accent="cyan" onClick={createConference} icon={Video}>Create & join</JewelButton>
          </>}
        >
          <div className="space-y-4">
            <JewelField label="Conference name">
              <JewelInput value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Team standup, customer call…" autoFocus />
            </JewelField>
            <JewelField label="Call type">
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setNewRoomType('video')}
                  className={`relative p-4 rounded-2xl border transition-all overflow-hidden ${
                    newRoomType === 'video' ? 'border-cyan-400/50 bg-cyan-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15]'
                  }`}>
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${newRoomType === 'video' ? 'bg-cyan-500/20' : 'bg-white/[0.03]'}`}>
                    <Video className={`w-5 h-5 ${newRoomType === 'video' ? 'text-cyan-300' : 'text-white/50'}`} />
                  </div>
                  <p className={`text-sm text-center font-medium ${newRoomType === 'video' ? 'text-white' : 'text-white/60'}`}>Video</p>
                  <p className="text-[10px] text-white/40 text-center mt-0.5">HD video + audio</p>
                </button>
                <button type="button" onClick={() => setNewRoomType('audio')}
                  className={`relative p-4 rounded-2xl border transition-all overflow-hidden ${
                    newRoomType === 'audio' ? 'border-cyan-400/50 bg-cyan-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15]'
                  }`}>
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${newRoomType === 'audio' ? 'bg-cyan-500/20' : 'bg-white/[0.03]'}`}>
                    <Mic className={`w-5 h-5 ${newRoomType === 'audio' ? 'text-cyan-300' : 'text-white/50'}`} />
                  </div>
                  <p className={`text-sm text-center font-medium ${newRoomType === 'audio' ? 'text-white' : 'text-white/60'}`}>Audio Only</p>
                  <p className="text-[10px] text-white/40 text-center mt-0.5">Lightweight, mobile-friendly</p>
                </button>
              </div>
            </JewelField>
            <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
              isCustomerCall ? 'border-purple-400/40 bg-purple-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
            }`}>
              <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors flex-shrink-0 ${
                isCustomerCall ? 'bg-purple-500/30 border-purple-400/60' : 'bg-white/[0.02] border-white/[0.15]'
              }`}>
                {isCustomerCall && <Check className="w-3 h-3 text-purple-300" />}
              </div>
              <input type="checkbox" checked={isCustomerCall} onChange={(e) => setIsCustomerCall(e.target.checked)} className="sr-only" />
              <div>
                <p className="text-white text-sm font-medium">Customer call</p>
                <p className="text-white/40 text-[11px]">Allow guests to join with a shareable link</p>
              </div>
            </label>
          </div>
        </JewelModal>
      </div>
    );
  }

  // Pre-join view — JEWEL
  if (roomId && !isJoined && roomInfo) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4 relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-1/4 left-1/3 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
        </div>
        <div className="w-full max-w-md relative overflow-hidden rounded-3xl border border-white/[0.08]"
          style={{
            background: 'linear-gradient(180deg, rgba(15,15,22,0.96) 0%, rgba(5,5,10,0.98) 100%)',
            boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7), 0 0 40px -8px rgba(0,229,255,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}>
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
          <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full blur-[100px] opacity-40 bg-cyan-500/30" />
          <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full blur-[100px] opacity-30 bg-violet-500/30" />
          <div className="relative p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/40" />
              <div className="absolute inset-0 rounded-3xl flex items-center justify-center">
                {roomInfo.call_type === 'video' ? <Video className="w-9 h-9 text-white" strokeWidth={1.5} /> : <Mic className="w-9 h-9 text-white" strokeWidth={1.5} />}
              </div>
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#0a0a14] animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 mb-1">Ready to join</p>
            <h2 className="text-2xl font-light text-white mb-1 tracking-tight">{roomInfo.name}</h2>
            <p className="text-white/50 text-sm mb-6">
              <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {roomInfo.participants} participant{roomInfo.participants !== 1 ? 's' : ''} inside</span>
            </p>
            {!currentUser && roomInfo.is_customer_call && (
              <div className="mb-4 text-left">
                <JewelField label="Your name" required>
                  <JewelInput value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Enter your name…" autoFocus />
                </JewelField>
              </div>
            )}
            <div className="flex gap-3">
              <JewelButton variant="ghost" onClick={() => { setRoomId(null); setRoomInfo(null); }} className="flex-1">Cancel</JewelButton>
              <JewelButton accent="cyan" onClick={joinConference} disabled={!currentUser && !guestName} icon={roomInfo.call_type === 'video' ? Video : Mic} className="flex-1">Join call</JewelButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // In-call view
  if (isJoined) {
    return (
      <div className="fixed inset-0 bg-gray-950 z-50">
        {/* Video grid */}
        <div className="relative h-[calc(100%-80px)] p-4">
          {/* Live reactions overlay */}
          <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
            {reactions.map(r => (
              <div key={r.id} className="absolute animate-reaction-float"
                style={{ left: `${r.x}%`, bottom: '4rem' }}>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-4xl drop-shadow-[0_4px_18px_rgba(0,0,0,0.7)]" style={{ filter: 'drop-shadow(0 0 12px rgba(217,70,239,0.35))' }}>{r.emoji}</span>
                  <span className="px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-sm text-white/90 text-[10px] tracking-wide">{r.from}</span>
                </div>
              </div>
            ))}
          </div>
          <div className={`grid gap-4 h-full ${
            participants.length <= 1 ? 'grid-cols-1' :
            participants.length <= 4 ? 'grid-cols-2' :
            'grid-cols-3'
          }`}>
            {/* Local video */}
            <div className="relative rounded-2xl overflow-hidden bg-gray-900 border border-gray-800">
              {roomInfo?.call_type === 'video' ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {(currentUser?.display_name || currentUser?.username || guestName || 'You').charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              <div className="absolute bottom-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white text-sm">
                You {isMuted && '(muted)'}
              </div>
              {isVideoOff && roomInfo?.call_type === 'video' && (
                <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                  <VideoOff className="w-12 h-12 text-gray-600" />
                </div>
              )}
            </div>

            {/* Remote participants */}
            {participants.filter(p => p !== currentUser?.username).map(participant => (
              <div key={participant} className="relative rounded-2xl overflow-hidden bg-gray-900 border border-gray-800">
                {roomInfo?.call_type === 'video' ? (
                  <video
                    id={`video-${participant}`}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">
                        {participant.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white text-sm">
                  {participant}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls bar — Jewel Rail */}
        <div className="absolute bottom-0 left-0 right-0 h-24 border-t border-white/[0.06] overflow-hidden"
          style={{ background: 'linear-gradient(180deg, rgba(10,10,20,0.85) 0%, rgba(5,5,10,0.98) 100%)', backdropFilter: 'blur(20px)' }}>
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
          <div className="pointer-events-none absolute -top-16 left-1/4 w-60 h-40 bg-cyan-500/[0.08] rounded-full blur-[80px]" />
          <div className="pointer-events-none absolute -top-16 right-1/4 w-60 h-40 bg-violet-500/[0.06] rounded-full blur-[80px]" />

          <div className="relative h-full flex items-center justify-between px-6">
            {/* Left - Room info */}
            <div className="flex items-center gap-3 min-w-0 max-w-xs">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                <Video className="w-4 h-4 text-cyan-300" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{roomInfo?.name}</p>
                <p className="text-white/40 text-[10px] flex items-center gap-1.5 mt-0.5">
                  <Users className="w-3 h-3" /> {participants.length} in call
                  {isRecording && <><span className="w-1 h-1 rounded-full bg-white/20 mx-0.5" /><span className="inline-flex items-center gap-1 text-rose-300"><span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />REC {formatTimer(recordingTimer)}</span></>}
                </p>
              </div>
            </div>

            {/* Center - Main controls */}
            <div className="flex items-center gap-2.5">
              <button onClick={toggleMute} data-testid="in-call-mute-btn"
                className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${
                  isMuted
                    ? 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-[0_0_24px_-4px_rgba(244,63,94,0.6)] text-white'
                    : 'bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-white/85 hover:border-white/[0.15]'
                }`}>
                {isMuted ? <MicOff className="w-5 h-5 group-hover:scale-110 transition-transform" /> : <Mic className="w-5 h-5 group-hover:scale-110 transition-transform" />}
              </button>

              {roomInfo?.call_type === 'video' && (
                <button onClick={toggleVideo} data-testid="in-call-video-btn"
                  className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${
                    isVideoOff
                      ? 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-[0_0_24px_-4px_rgba(244,63,94,0.6)] text-white'
                      : 'bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-white/85 hover:border-white/[0.15]'
                  }`}>
                  {isVideoOff ? <VideoOff className="w-5 h-5 group-hover:scale-110 transition-transform" /> : <Video className="w-5 h-5 group-hover:scale-110 transition-transform" />}
                </button>
              )}

              <button onClick={toggleHandRaise} data-testid="hand-raise-btn"
                className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${
                  handRaised
                    ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-[0_0_24px_-4px_rgba(251,191,36,0.6)] text-white'
                    : 'bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-white/85 hover:border-white/[0.15]'
                }`} title="Raise Hand">
                <Hand className={`w-5 h-5 transition-transform ${handRaised ? 'animate-bounce' : 'group-hover:scale-110'}`} />
              </button>

              {/* Reactions */}
              <div className="relative">
                <button onClick={() => setShowReactionPicker(v => !v)} data-testid="reaction-btn"
                  className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${showReactionPicker ? 'bg-fuchsia-500/[0.14] border border-fuchsia-400/40 shadow-[0_0_18px_-4px_rgba(217,70,239,0.6)] text-fuchsia-200' : 'bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-white/85 hover:border-white/[0.15]'}`}
                  title="Send a reaction">
                  <span className="text-lg group-hover:scale-110 transition-transform">😊</span>
                </button>
                {showReactionPicker && (
                  <div data-testid="reaction-picker" className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1.5 rounded-2xl bg-[#0a0a14]/95 backdrop-blur-xl border border-white/[0.10] shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7)]"
                    style={{ background: 'linear-gradient(135deg, rgba(15,10,25,0.95), rgba(10,10,20,0.98))' }}>
                    {['❤️', '👏', '🔥', '🎉', '😂', '👍', '💯', '🤯'].map(e => (
                      <button key={e} onClick={() => sendReaction(e)} data-testid={`reaction-${e}`}
                        className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/[0.08] hover:scale-125 transition-all text-xl">
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Record */}
              <button onClick={toggleRecording} data-testid="record-btn"
                className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${
                  isRecording
                    ? 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-[0_0_28px_-2px_rgba(244,63,94,0.7)] text-white animate-pulse'
                    : 'bg-white/[0.04] border border-white/[0.08] hover:bg-rose-500/[0.10] hover:border-rose-400/40 text-white/85'
                }`} title={isRecording ? 'Stop recording' : 'Record meeting'}>
                {isRecording ? (
                  <span className="w-3.5 h-3.5 rounded-sm bg-white" />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-current group-hover:border-rose-300" />
                )}
              </button>

              {/* Divider */}
              <div className="w-px h-8 bg-white/[0.08] mx-1" />

              <button onClick={leaveConference} data-testid="in-call-leave-btn"
                className="relative px-5 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white shadow-[0_10px_30px_-8px_rgba(244,63,94,0.6)] transition-all group flex items-center gap-2">
                <PhoneOff className="w-5 h-5 group-hover:rotate-[135deg] transition-transform" />
                <span className="text-[11px] font-semibold tracking-[0.05em]">Leave</span>
              </button>
            </div>

            {/* Right - Extra controls */}
            <div className="flex items-center gap-2">
              {/* Meeting Timer */}
              <div className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/[0.03] border border-white/[0.06]" data-testid="meeting-timer">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-white/80 text-xs font-mono tracking-wider">{formatTimer(meetingTimer)}</span>
              </div>

              <button onClick={() => { setShowChatPanel(!showChatPanel); setShowNotesPanel(false); setShowRecordingsPanel(false); if (!showChatPanel) fetchMeetingChat(); }}
                data-testid="meeting-chat-btn"
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all border ${
                  showChatPanel ? 'bg-cyan-500/[0.15] border-cyan-400/40 text-cyan-300 shadow-[0_0_16px_-4px_rgba(0,229,255,0.4)]' : 'bg-white/[0.03] border-white/[0.06] text-white/60 hover:text-white hover:border-white/[0.15]'
                }`}><MessageSquare className="w-4 h-4" /></button>

              <button onClick={() => { setShowNotesPanel(!showNotesPanel); setShowChatPanel(false); setShowRecordingsPanel(false); if (!showNotesPanel) fetchMeetingNotes(); }}
                data-testid="meeting-notes-btn"
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all border ${
                  showNotesPanel ? 'bg-amber-500/[0.15] border-amber-400/40 text-amber-300 shadow-[0_0_16px_-4px_rgba(251,191,36,0.4)]' : 'bg-white/[0.03] border-white/[0.06] text-white/60 hover:text-white hover:border-white/[0.15]'
                }`}><Settings className="w-4 h-4" /></button>

              <button onClick={() => { setShowRecordingsPanel(!showRecordingsPanel); setShowChatPanel(false); setShowNotesPanel(false); if (!showRecordingsPanel) fetchRecordings(); }}
                data-testid="recordings-panel-btn"
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all border ${
                  showRecordingsPanel ? 'bg-rose-500/[0.15] border-rose-400/40 text-rose-300 shadow-[0_0_16px_-4px_rgba(244,63,94,0.4)]' : 'bg-white/[0.03] border-white/[0.06] text-white/60 hover:text-white hover:border-white/[0.15]'
                }`} title="Recordings"><FileText className="w-4 h-4" /></button>

              <button onClick={copyInviteLink}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all border bg-white/[0.03] border-white/[0.06] text-white/60 hover:text-white hover:border-white/[0.15]"
                title="Copy invite">
                {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
              </button>
              {roomInfo?.created_by === currentUser?.username && (
                <button onClick={endConference}
                  className="h-10 px-3.5 rounded-xl flex items-center gap-1.5 transition-all bg-rose-500/[0.08] border border-rose-500/30 text-rose-300 hover:bg-rose-500/[0.15] text-[10px] tracking-[0.1em] uppercase font-semibold">
                  End for all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Side Panels */}
        {(showChatPanel || showNotesPanel || showRecordingsPanel) && (
          <div className="w-80 bg-gray-900 border-l border-gray-800 flex flex-col">
            {showChatPanel && (
              <>
                <div className="p-3 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="text-white font-medium text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-400" /> Meeting Chat
                  </h3>
                  <button onClick={() => setShowChatPanel(false)} className="text-gray-500 hover:text-white">
                    <ShieldX className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {chatMessages.length === 0 ? (
                    <p className="text-gray-600 text-xs text-center py-4">No messages yet</p>
                  ) : chatMessages.map((msg, i) => (
                    <div key={i} className="bg-gray-800/50 rounded-lg p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-cyan-400 text-[10px] font-bold">{msg.sender_name}</span>
                        <span className="text-gray-600 text-[9px]">{msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                      <p className="text-gray-300 text-xs">{msg.message}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-800 flex gap-2">
                  <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type a message..." data-testid="meeting-chat-input"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/30" />
                  <Button onClick={sendChatMessage} size="sm" className="bg-cyan-500 hover:bg-cyan-600 px-3">
                    <MessageSquare className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </>
            )}
            {showNotesPanel && (
              <>
                <div className="p-3 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="text-white font-medium text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4 text-amber-400" /> Meeting Notes
                  </h3>
                  <button onClick={() => setShowNotesPanel(false)} className="text-gray-500 hover:text-white">
                    <ShieldX className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 p-3">
                  <textarea value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)}
                    placeholder="Take meeting notes here..." data-testid="meeting-notes-textarea"
                    className="w-full h-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500/30 resize-none" />
                </div>
                <div className="p-3 border-t border-gray-800">
                  <Button onClick={saveMeetingNotes} className="w-full bg-amber-500 hover:bg-amber-600 text-sm" data-testid="save-notes-btn">
                    Save Notes
                  </Button>
                </div>
              </>
            )}
            {showRecordingsPanel && (
              <>
                <div className="p-3 border-b border-white/[0.06] flex items-center justify-between bg-black/40">
                  <h3 className="text-white font-medium text-sm flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-rose-500/20 border border-rose-500/30 flex items-center justify-center">
                      <FileText className="w-3.5 h-3.5 text-rose-300" />
                    </div>
                    Recordings & Transcripts
                  </h3>
                  <button onClick={() => setShowRecordingsPanel(false)} className="w-7 h-7 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.06] transition-all">
                    <ShieldX className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-3 border-b border-white/[0.06] space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                    <input
                      value={recordingsQuery}
                      onChange={(e) => setRecordingsQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchRecordings()}
                      placeholder="Search all transcripts…"
                      data-testid="recordings-search-input"
                      className="w-full pl-9 pr-3 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white text-xs placeholder-white/30 focus:outline-none focus:border-rose-400/40"
                    />
                  </div>
                  {isRecording && (
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-rose-500/[0.08] border border-rose-500/25">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                      <span className="text-rose-300 text-[10px] tracking-[0.1em] uppercase font-semibold">Recording · {formatTimer(recordingTimer)}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2 jewel-scroll">
                  {(recordingsQuery.trim() ? recordingsSearch : recordings).length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-white/15 mx-auto mb-2" />
                      <p className="text-white/40 text-xs">{recordingsQuery.trim() ? 'No matches' : 'No recordings yet'}</p>
                      <p className="text-white/25 text-[10px] mt-1">Tap the record button to capture this call</p>
                    </div>
                  ) : (recordingsQuery.trim() ? recordingsSearch : recordings).map(r => (
                    <div key={r.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-rose-400/25 transition-all">
                      <button onClick={() => setSelectedRecording(selectedRecording?.id === r.id ? null : r)} className="w-full p-2.5 text-left">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-xs font-medium truncate">{r.room_name || 'Untitled'}</p>
                            <p className="text-white/40 text-[10px] mt-0.5">
                              {r.created_at ? new Date(r.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              {' · '}{formatTimer(r.duration_seconds || 0)}
                              {r.word_count ? ` · ${r.word_count} words` : ''}
                            </p>
                          </div>
                          <ChevronRight className={`w-3.5 h-3.5 text-white/30 transition-transform ${selectedRecording?.id === r.id ? 'rotate-90' : ''}`} />
                        </div>
                      </button>
                      {selectedRecording?.id === r.id && (
                        <div className="border-t border-white/[0.06] p-2.5 bg-black/30">
                          <div className="max-h-40 overflow-y-auto text-white/70 text-[11px] leading-relaxed jewel-scroll whitespace-pre-wrap">
                            {r.transcript || '(No transcript)'}
                          </div>
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/[0.05]">
                            <button onClick={() => { navigator.clipboard.writeText(r.transcript || ''); toast.success('Transcript copied'); }}
                              className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-white/60 hover:text-white text-[10px]">
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                            {r.recorded_by === currentUser?.username && (
                              <button onClick={() => deleteRecording(r.id)}
                                data-testid={`delete-recording-${r.id}`}
                                className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md bg-rose-500/[0.06] border border-rose-500/20 text-rose-300 hover:bg-rose-500/[0.12] text-[10px]">
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {raisedHands.length > 0 && (
          <div className="absolute top-4 right-4 bg-amber-500/20 border border-amber-500/40 rounded-xl p-3 max-w-xs" data-testid="raised-hands">
            <p className="text-amber-400 text-xs font-medium mb-1 flex items-center gap-1"><Hand className="w-3.5 h-3.5" /> Hands Raised</p>
            {raisedHands.map((name, i) => (
              <span key={i} className="text-white text-xs mr-2">{name}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default ConferencePage;
